import { type fabric } from "fabric"
import GroupObject from "../objects/group"
import { isMobile } from "@/common/util"
import Calc from "@/common/util/calculator"
import BaseFixmethod, { type FixmethodPosition } from "@/signomatic/app/fixmethods/base"
import CustomHolesFixmethod from "@/signomatic/app/fixmethods/custom_holes"
import { flagEnabled } from "@/signomatic/app/flags"
import CircleObject from "@/signomatic/app/objects/circle"
import ImageObject from "@/signomatic/app/objects/image"
import PathObject from "@/signomatic/app/objects/path"
import RectObject from "@/signomatic/app/objects/rect"

export default abstract class BaseScrewFixmethod extends BaseFixmethod {
  get count() {
    const { material, shape } = this.state
    const customHolesFlag = flagEnabled("custom-holes-9377")
    const customHoles = new CustomHolesFixmethod(this.drawables)
    const labelPlates = (this.drawables.signType === "label_plates" || material === "stainless") && material !== "brass"
    const rectShape = shape === "rect"

    const positions = customHolesFlag && labelPlates && rectShape ? customHoles.positions() : this.positions()

    return positions.filter(e => !e.stretch).length
  }

  topDrawable() {
    const { widthPx, heightPx, material, shape } = this.state
    const customHolesFlag = flagEnabled("custom-holes-9377")
    const holesOverlayFlag = flagEnabled("holes-overlay-9389")
    const customHoles = new CustomHolesFixmethod(this.drawables)
    const customHoleTabOpened = this.drawables.isCustomHoleTabOpened
    const labelPlates = (this.drawables.signType === "label_plates" || material === "stainless") && material !== "brass"
    const rectShape = shape === "rect"

    const positions = customHolesFlag && labelPlates && rectShape ? customHoles.positions() : this.positions()
    let items: fabric.Object[] = []

    for (const position of positions) {
      items = [...items, ...this.getItem(position)]
    }

    if (customHolesFlag && holesOverlayFlag && labelPlates && rectShape && customHoleTabOpened && !isMobile()) {
      customHoles.addCustomHoleDimensions()
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
      }),
    ]
  }

  engravingDrawable() {
    const { widthPx, heightPx, material, shape } = this.state
    const customHolesFlag = flagEnabled("custom-holes-9377")
    const customHoles = new CustomHolesFixmethod(this.drawables)
    const labelPlates = (this.drawables.signType === "label_plates" || material === "stainless") && material !== "brass"
    const rectShape = shape === "rect"

    const positions = customHolesFlag && labelPlates && rectShape ? customHoles.positions() : this.positions()
    let items: fabric.Object[] = []

    for (const position of positions) {
      items = [...items, ...this.getEngravingItem(position)]
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
      }),
    ]
  }

  autoSizePadding(state: SignState) {
    const { widthPx, height, heightPx, shape } = state
    const fourScrews = height >= 100
    const extra = fourScrews && shape === "round" ? Math.min(widthPx, heightPx) * 0.1 : 0
    return (this.drawables.signType === "acryl_sign" ? 100 : 50) + extra
  }

  abstract positions(): FixmethodPosition[]

  protected imagePaths() {
    return "/images/signomatic/fix_methods/screw_capped.png"
  }

  private getItem(e: FixmethodPosition) {
    return this.state.fixmethod === "screw_capped" ? this.getCappedItem(e) : this.getRegularItem(e)
  }

  private getRegularItem(e: FixmethodPosition) {
    const { material, color, badgeName } = this.state
    const items = []
    const outerWidth = 1

    let headFill = "#C6C6C6"

    if (this.drawables.signType === "brass_sign" || material === "brass") {
      if (material === "nickel") {
        headFill = badgeName === "paris" ? "#595759" : "#c9c5c7"
      } else if (material === "brass" && color === "#ffe472") {
        headFill = "#d8c98d"
      } else {
        headFill = color
      }
    }

    if (e.stretch === true) {
      items.push(
        new CircleObject({
          left: e.left,
          top: e.top,
          radius: (e.diameter || 0) / 2,
          stroke: "transparent",
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: "transparent",
        }).addClass("stretch")
      )
    } else {
      const left = (e.left || 0) - ((e.headDiameter || 0) - (e.diameter || 0)) / 2
      const top = (e.top || 0) - ((e.headDiameter || 0) - (e.diameter || 0)) / 2

      // head
      items.push(
        new CircleObject({
          left,
          top,
          radius: (e.headDiameter || 0) / 2,
          stroke: "#878787",
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: headFill,
        })
      )

      if (this.drawables.signType === "ecoboard") {
        // crosscut
        items.push(
          new PathObject("M0,3.78H7.56M3.78,0V7.56", {
            originX: "center",
            originY: "center",
            left: left + (e.headDiameter || 0) / 2 - (e.headDiameter || 0) / 4 + outerWidth / 2 + Calc.mmToPx(1.75),
            top: top + (e.headDiameter || 0) / 2 - (e.headDiameter || 0) / 28 + outerWidth / 2 + Calc.mmToPx(0.25),
            stroke: "transparent",
            scaleX: 1,
            scaleY: 1,
          }).addClass("hang", "screw")
        )
      } else {
        // hole
        items.push(
          new CircleObject({
            left: (e.left || 0) + outerWidth / 2,
            top: (e.top || 0) + outerWidth / 2,
            radius: (e.diameter || 0) / 2,
            stroke: "none",
            strokeWidth: 0,
            startAngle: 0,
            endAngle: Calc.toDeg(Math.PI * 2),
            fill: "transparent",
          }).addClass("hang", "screw")
        )
      }

      if (e.flathead === true) {
        items.push(
          new RectObject({
            left,
            top: top + (e.headDiameter || 0) / 2 - (e.headDiameter || 0) / 12,
            width: (e.headDiameter || 0) + outerWidth,
            height: (e.headDiameter || 0) / 6,
            rx: 1,
            ry: 1,
            strokeWidth: 0,
            fill: "#878787",
          })
        )
      } else {
        // horizontal
        items.push(
          new RectObject({
            left: left + (e.headDiameter || 0) / 2 - (e.headDiameter || 0) / 4 + outerWidth / 2,
            top: top + (e.headDiameter || 0) / 2 - (e.headDiameter || 0) / 12 + outerWidth / 2,
            width: (e.headDiameter || 0) / 2,
            height: (e.headDiameter || 0) / 6,
            rx: 1,
            ry: 1,
            strokeWidth: 0,
            fill: "#878787",
          })
        )

        // vertical
        items.push(
          new RectObject({
            left: left + (e.headDiameter || 0) / 2 - (e.headDiameter || 0) / 12 + outerWidth / 2,
            top: top + (e.headDiameter || 0) / 2 - (e.headDiameter || 0) / 4 + outerWidth / 2,
            width: (e.headDiameter || 0) / 6,
            height: (e.headDiameter || 0) / 2,
            rx: 1,
            ry: 1,
            strokeWidth: 0,
            fill: "#878787",
          })
        )
      }
    }

    return items
  }

  private getCappedItem(e: FixmethodPosition) {
    const { widthPx, heightPx } = this.state
    const items = []
    const outerWidth = 1
    const centerX = widthPx / 2
    const centerY = heightPx / 2

    if (e.stretch === true) {
      items.push(
        new CircleObject({
          left: e.left,
          top: e.top,
          radius: (e.diameter || 0) / 2,
          stroke: "transparent",
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: "transparent",
        }).addClass("stretch")
      )
    } else {
      const { width, height } = this.state
      const left = (e.left || 0) - ((e.headDiameter || 0) - (e.diameter || 0)) / 2
      const top = (e.top || 0) - ((e.headDiameter || 0) - (e.diameter || 0)) / 2
      const sizeTreshold = width > 399 || height > 399

      // head
      if (this.imagesLoaded) {
        if (left + (e.headDiameter || 0) < centerX) {
          items.push(
            new CircleObject({
              left: left + 2,
              top,
              radius: (e.headDiameter || 0) / 2,
              strokeWidth: 0,
              startAngle: 0,
              endAngle: Calc.toDeg(Math.PI * 2),
              fill: "#B8B7B7",
            })
          )
        } else if (left > centerX) {
          items.push(
            new CircleObject({
              left: left - 2,
              top,
              radius: (e.headDiameter || 0) / 2,
              strokeWidth: 0,
              startAngle: 0,
              endAngle: Calc.toDeg(Math.PI * 2),
              fill: "#B8B7B7",
            })
          )
        } else if (top < centerY) {
          items.push(
            new CircleObject({
              left,
              top: top + 2,
              radius: (e.headDiameter || 0) / 2,
              strokeWidth: 0,
              startAngle: 0,
              endAngle: Calc.toDeg(Math.PI * 2),
              fill: "#B8B7B7",
            })
          )
        }

        const img = this.image()

        if (img) {
          items.push(
            new ImageObject(img, {
              left,
              top,
              scaleX: (e.headDiameter || 0) / (img.naturalWidth || img.width),
              scaleY: (e.headDiameter || 0) / (img.naturalHeight || img.height),
            })
          )
        }
      }

      if (this.drawables.signType === "ecoboard") {
        // crosscut
        items.push(
          new PathObject("M0,3.78H7.56M3.78,0V7.56", {
            originX: "center",
            originY: "center",
            left:
              left +
              (e.headDiameter || 0) / 2 -
              (e.headDiameter || 0) / 4 +
              outerWidth / 2 +
              (sizeTreshold ? Calc.mmToPx(3.75) : Calc.mmToPx(2.5)),
            top:
              top +
              (e.headDiameter || 0) / 2 -
              (e.headDiameter || 0) / 28 +
              outerWidth / 2 +
              (sizeTreshold ? Calc.mmToPx(0.5) : Calc.mmToPx(0.375)),
            stroke: "transparent",
            scaleX: 1,
            scaleY: 1,
          }).addClass("hang", "screw")
        )
      } else {
        // hole
        items.push(
          new CircleObject({
            left: (e.left || 0) + outerWidth / 2,
            top: (e.top || 0) + outerWidth / 2,
            radius: (e.diameter || 0) / 2,
            stroke: "none",
            strokeWidth: 0,
            startAngle: 0,
            endAngle: Calc.toDeg(Math.PI * 2),
            fill: "transparent",
          }).addClass("hang", "screw")
        )
      }
    }

    return items
  }

  private getEngravingItem(e: FixmethodPosition) {
    const stroke = this.drawables.engravingColor
    const fill = "transparent"
    const outerWidth = this.drawables.engravingStrokeWidth
    const items = []

    if (e.stretch === true) {
      items.push(
        new CircleObject({
          left: e.left,
          top: e.top,
          radius: (e.diameter || 0) / 2,
          stroke: "transparent",
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: "transparent",
        }).addClass("stretch")
      )
    } else {
      items.push(
        new CircleObject({
          left: e.left,
          top: e.top,
          radius: (e.diameter || 0) / 2,
          stroke,
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill,
        }).addClass("hang", "screw")
      )
    }

    return items
  }
}
